import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogModule as MatDialogModule,
} from '@angular/material/legacy-dialog';
import { FieldDef, SavedView, QueryConfig } from 'app/core/models';
import { TableSortState } from '@shared/models/state-tracking.model';
import { RelatedToType } from '@shared/enums';
import { OrganismModule } from '@shared/components/organism/organism.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PropertyTenantsAddTenantComponent } from '../tenants/modals/add-tenant/add-tenant.component';
import GraphqlService from '@services/graphql.service';
import { GraphqlParams, GraphqlResultInfo, GraphqlResultPageInfo } from '@shared/models/graphql';
import { TsRxjsOperators } from '@shared/Utils';
import { TsSnackbarService } from 'tsui';
import { tap, switchMap, map, take, Observable, noop, filter, catchError, of } from 'rxjs';
import { MessagesUtils } from '@shared/Utils/messages.utils';
import { GraphqlParamsBuilder } from '@services/graphql-params.builder';
import { PropertyTenantsService } from '../tenants/tenants.service';

type PropertyLeasesData = {
  module: string;
  initialData: Record<string, unknown>;
  account: any;
  options: any[];
  fieldDefs: FieldDef[];
  property: Record<string, unknown>;
};

type PropertyLeasesDataResponse = {
  pagination: GraphqlResultPageInfo;
  data: any[];
};

@Component({
  selector: 'properties-leases',
  templateUrl: './leases.component.html',
  styleUrls: ['./leases.component.scss'],
  standalone: true,
  imports: [CommonModule, OrganismModule, MatDialogModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeasesComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly matDialog = inject(MatDialog);
  private readonly graphqlService = inject(GraphqlService);
  private readonly snackbarService = inject(TsSnackbarService);
  private readonly propertyTenantsService = inject(PropertyTenantsService);

  private readonly defaultLeasesColumns: string[] = [
    'name',
    'base_monthly_rent',
    'current_monthly_rent',
    'current_annual_rent',
    'starting_annual_rent',
    'starting_monthly_rent',
    'lease_area',
    'lease_start_date',
    'lease_end_date',
  ];

  // Required inputs for filter-table
  readonly title: string = 'Leases';
  propertyLeasesData: WritableSignal<PropertyLeasesData> = signal({} as PropertyLeasesData);
  savedView: WritableSignal<SavedView> = signal(null as SavedView);
  queryFilters: WritableSignal<Partial<QueryConfig.QueryFilters>> = signal({});
  fieldDefs: WritableSignal<FieldDef[]> = signal([]);
  displayColumns: WritableSignal<string[]> = signal(this.defaultLeasesColumns);
  dataset: WritableSignal<any[]> = signal([]);
  // filter-table adds 1 to the page number
  pagination: WritableSignal<any> = signal({ page: 0, size: 20 } as any);
  sortState: WritableSignal<TableSortState> = signal({
    active: 'name',
    direction: 'asc',
  });
  isLoading: WritableSignal<boolean> = signal(false);
  addNewObjectConfig = {
    label: 'Add Lease',
    tooltip: 'Add a new lease',
  };
  relatedToType: RelatedToType = RelatedToType.PropertyTenant;
  graphqlConnection: string = 'PropertyTenant';

  ngOnInit() {
    this.activatedRoute.data
      .pipe(
        tap((data: any) => {
          this.initRouteData(data);
        }),
        switchMap(() => {
          return this.reloadData();
        }),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbarService,
          MessagesUtils.Success.LOAD('Leases'),
          MessagesUtils.Error.LOAD('Leases'),
        ),
        take(1),
      )
      .subscribe((data: any) => {
        this.updatePropertyLeasesData(data);
        this.isLoading.set(false);
      });
  }

  private get currentProperty(): Record<string, any> {
    return this.propertyLeasesData().property;
  }

  private get defaultFilters(): [string, string, string] {
    return ['property_id', 'eq', this.currentProperty.id.toString()];
  }

  private get requestFields(): string[] {
    return [...this.displayColumns(), 'id'];
  }

  page({ activePage, pageSize }: any): void {
    const params = GraphqlParamsBuilder.forConnection(this.graphqlConnection)
      .withFieldDefs(this.fieldDefs())
      .withPagination(activePage, pageSize)
      .withFields(this.requestFields)
      .withFilterJson(this.queryFilters().fjson)
      .withQuery(this.queryFilters().q)
      .build();

    this.loadPaginatedPropertyLeases(params).subscribe(noop);
  }

  sort({ active, direction }: any): void {
    this.sortState.set({ active, direction });

    this.reloadData().subscribe(noop);
  }

  filter({ fjson }: any): void {
    const parsedFjson = JSON.parse(fjson);
    const filters = [this.defaultFilters, ...parsedFjson];
    const fjsonString = JSON.stringify(filters);

    this.queryFilters.update((prev) => ({ ...prev, fjson: fjsonString }));

    this.reloadData().subscribe(noop);
  }

  search({ q }: any): void {
    this.queryFilters.update((prev) => ({ ...prev, q }));

    this.reloadData().subscribe(noop);
  }

  update({ item }: any): void {
    this.isLoading.set(true);

    this.propertyTenantsService
      .updatePropertyLease(this.currentProperty.id, { ...item })
      .pipe(
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbarService,
          MessagesUtils.Success.UPDATE('Lease'),
          MessagesUtils.Error.UPDATE('Lease'),
        ),
        tap(() => this.isLoading.set(false)),
        switchMap(() => this.reloadData()),
      )
      .subscribe(noop);
  }

  onNewObjectAdded(): void {
    this.matDialog
      .open(PropertyTenantsAddTenantComponent, {
        panelClass: 'mailbox-compose-dialog',
        disableClose: true,
        data: {
          data: this.propertyLeasesData(),
          property: this.currentProperty,
        },
      })
      .afterClosed()
      .pipe(
        filter((result) => result),
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.reloadData()),
      )
      .subscribe(noop);
  }

  private initRouteData(data: any) {
    this.propertyLeasesData.set(data);
    // TODO: Set saved view once we have an Object like PropertyDeals available to attach saved-views to
    // this.savedView.set({
    //   id: 1,
    //   name: 'Property Leases View',
    //   definition: JSON.stringify({
    //     table: {
    //       fields: this.displayColumns(),
    //       sort: this.sortState,
    //     },
    //   }),
    //   related_to_type: RelatedToType.PropertyTenant,
    //   visiblity: 'true',
    //   account_id: 1,
    // });
    this.fieldDefs.set(data.fieldDefs);
    this.queryFilters.update((prev) => ({ ...prev, fjson: JSON.stringify([this.defaultFilters]) }));
  }

  private updatePropertyLeasesData({ pagination, data }: PropertyLeasesDataResponse): void {
    this.pagination.set({
      length: pagination?.filteredCount,
      size: pagination?.pageSize,
      page: pagination?.currentPage - 1,
    });

    this.dataset.set(data);
  }

  private reloadData(): Observable<PropertyLeasesDataResponse> {
    const params = GraphqlParamsBuilder.forConnection(this.graphqlConnection)
      .withFieldDefs(this.fieldDefs())
      .withPagination(this.pagination().page + 1, this.pagination().size)
      .withFields(this.requestFields)
      .withFilterJson(this.queryFilters().fjson)
      .withSort([{ field: this.sortState().active, order: this.sortState().direction }])
      .withQuery(this.queryFilters().q)
      .build();

    return this.loadPaginatedPropertyLeases(params);
  }

  private loadPaginatedPropertyLeases(params: GraphqlParams): Observable<PropertyLeasesDataResponse> {
    this.isLoading.set(true);

    return this.graphqlService.query(params as GraphqlParams).pipe(
      map((res: GraphqlResultInfo<any>) => {
        return { pagination: res.pageInfo, data: GraphqlService.extractNodeData(res) };
      }),
      tap((data: PropertyLeasesDataResponse) => {
        this.updatePropertyLeasesData(data);
        this.isLoading.set(false);
      }),
      TsRxjsOperators.successAndErrorSnackbar(this.snackbarService, undefined, MessagesUtils.Error.LOAD('Leases')),
      take(1),
      catchError((error) => {
        this.isLoading.set(false);
        return of(null);
      }),
    );
  }
}
